import { createSelector } from '@app/utils/createSelector'
import { catchFetchDataError } from '@app/utils/performFetchData'

import { getSittersPromoted } from '@app/packages/api/v2/actions/sitters'
import { getUsersByFilter } from '@app/packages/api/v2/actions/users'
import type { ApiPromotedSitter } from '@app/packages/api/v2/types/entities'
import { createPromise } from '@app/packages/thunkSelector/promiseState'
import { createLoadable } from '@app/packages/thunkSelector/thunkSelector'

import { callV2Api } from '@app/store/actions/api'
import { createReduxSlice } from '@app/store/redux_slice'
import { routingUUIDSelector } from '@app/store/slices/routing'
import { setUsersFromResponseSlice, usersModelsSelector as usersSelector } from '@app/store/slices/users'

const sittersPromotedSlice = createReduxSlice<{ routing_uuid: string; list: ApiPromotedSitter[] }>('sitters_promoted')

const sittersPromotedResultSelector = createSelector([sittersPromotedSlice.selector, routingUUIDSelector], (meta, uuid) =>
  createLoadable(c => {
    if (!meta || meta.routing_uuid !== uuid) {
      return c.load(
        createPromise('Sitters promoted must be fetched', uuid, async dispatch => {
          const resp = await dispatch(callV2Api(getSittersPromoted())).catch(catchFetchDataError(dispatch, 'Promoted sitters fetch failed'))
          const list = resp.data
          const ids = list.map(s => String(s.attributes.id))
          dispatch(setUsersFromResponseSlice(await dispatch(callV2Api(getUsersByFilter({ ids, account_type: 'sitter' })))))
          dispatch(sittersPromotedSlice.set({ routing_uuid: uuid, list }))
        })
      )
    }
    return meta.list
  })
)

export const sittersPromotedListResultSelector = createSelector([sittersPromotedResultSelector, usersSelector], (promotedResult, users) =>
  createLoadable(c => {
    const promoted = c.unwrap(promotedResult)
    return promoted.map(model => {
      const user = users[model.attributes.id]
      if (!user) throw new Error(`User with id ${model.attributes.id} not found`)
      if (user.account_type !== 'sitter') throw new Error(`User with id ${model.attributes.id} is not sitter`)
      return { promoted: model, user }
    })
  })
)
